import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Plus, Trash2, Wand2 } from 'lucide-react';
import { botsApi } from '@/api/bots';
import { unwrapError } from '@/api/client';
import type { BotPromptExample, BotPromptSections } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { normalizePromptSections } from '@/lib/prompt-sections';

export function BotPromptEditor({
  value,
  onChange,
}: {
  value: BotPromptSections;
  onChange: (next: BotPromptSections) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const set = <K extends keyof BotPromptSections>(key: K, v: BotPromptSections[K]): void =>
    onChange({ ...value, [key]: v });

  const templates = useQuery({
    queryKey: ['bot-default-prompts'],
    queryFn: () => botsApi.defaultPrompts(),
    staleTime: 5 * 60_000,
  });

  const applyTemplate = (tpl: BotPromptSections | undefined): void => {
    if (!tpl) return;
    const hasContent = Object.keys(normalizePromptSections(value)).length > 0;
    if (hasContent && !window.confirm(t('organization.bots.dialog.prompt.templateConfirm'))) return;
    onChange({ ...tpl });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{t('organization.bots.dialog.prompt.title')}</p>
          <p className="text-xs text-muted-foreground">{t('organization.bots.dialog.prompt.intro')}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="shrink-0 gap-1.5">
              <Wand2 className="h-3.5 w-3.5" />
              {t('organization.bots.dialog.prompt.template')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={!templates.data}
              onClick={() => applyTemplate(templates.data?.templates.ops)}
            >
              {t('organization.bots.dialog.prompt.templateOps')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!templates.data}
              onClick={() => applyTemplate(templates.data?.templates.support)}
            >
              {t('organization.bots.dialog.prompt.templateSupport')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TextSection
        title={t('organization.bots.dialog.prompt.role.title')}
        help={t('organization.bots.dialog.prompt.role.help')}
        placeholder={t('organization.bots.dialog.prompt.role.placeholder')}
        value={value.role ?? ''}
        onChange={(v) => set('role', v)}
        defaultOpen
      />
      <TextSection
        title={t('organization.bots.dialog.prompt.context.title')}
        help={t('organization.bots.dialog.prompt.context.help')}
        placeholder={t('organization.bots.dialog.prompt.context.placeholder')}
        value={value.context ?? ''}
        onChange={(v) => set('context', v)}
      />
      <TextSection
        title={t('organization.bots.dialog.prompt.tone.title')}
        help={t('organization.bots.dialog.prompt.tone.help')}
        placeholder={t('organization.bots.dialog.prompt.tone.placeholder')}
        value={value.tone ?? ''}
        onChange={(v) => set('tone', v)}
        rows={2}
      />
      <ListSection
        title={t('organization.bots.dialog.prompt.rules.title')}
        help={t('organization.bots.dialog.prompt.rules.help')}
        placeholder={t('organization.bots.dialog.prompt.rules.placeholder')}
        addLabel={t('organization.bots.dialog.prompt.rules.add')}
        items={value.rules ?? []}
        onChange={(v) => set('rules', v)}
        defaultOpen
      />
      <ListSection
        title={t('organization.bots.dialog.prompt.forbidden.title')}
        help={t('organization.bots.dialog.prompt.forbidden.help')}
        placeholder={t('organization.bots.dialog.prompt.forbidden.placeholder')}
        addLabel={t('organization.bots.dialog.prompt.forbidden.add')}
        items={value.forbidden ?? []}
        onChange={(v) => set('forbidden', v)}
      />
      <TextSection
        title={t('organization.bots.dialog.prompt.tools.title')}
        help={t('organization.bots.dialog.prompt.tools.help')}
        placeholder={t('organization.bots.dialog.prompt.tools.placeholder')}
        value={value.tools ?? ''}
        onChange={(v) => set('tools', v)}
      />
      <TextSection
        title={t('organization.bots.dialog.prompt.escalation.title')}
        help={t('organization.bots.dialog.prompt.escalation.help')}
        placeholder={t('organization.bots.dialog.prompt.escalation.placeholder')}
        value={value.escalation ?? ''}
        onChange={(v) => set('escalation', v)}
        rows={2}
      />
      <TextSection
        title={t('organization.bots.dialog.prompt.outputFormat.title')}
        help={t('organization.bots.dialog.prompt.outputFormat.help')}
        placeholder={t('organization.bots.dialog.prompt.outputFormat.placeholder')}
        value={value.outputFormat ?? ''}
        onChange={(v) => set('outputFormat', v)}
      />
      <ExamplesSection examples={value.examples ?? []} onChange={(v) => set('examples', v)} />
      <TextSection
        title={t('organization.bots.dialog.prompt.freeform.title')}
        help={t('organization.bots.dialog.prompt.freeform.help')}
        placeholder={t('organization.bots.dialog.prompt.freeform.placeholder')}
        value={value.freeform ?? ''}
        onChange={(v) => set('freeform', v)}
        rows={6}
        mono
      />

      <PromptPreviewPanel value={value} />
    </div>
  );
}

function SectionShell({
  title,
  filled,
  defaultOpen,
  children,
}: {
  title: string;
  filled: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(Boolean(defaultOpen) || filled);
  React.useEffect(() => {
    if (filled) setOpen(true);
  }, [filled]);
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-muted/50"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span className="flex-1">{title}</span>
        {filled ? (
          <Badge variant="success" className="text-[10px]">
            {t('organization.bots.dialog.prompt.filled')}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">
            {t('organization.bots.dialog.prompt.empty')}
          </Badge>
        )}
      </button>
      {open ? <div className="space-y-2 border-t px-3 py-3">{children}</div> : null}
    </div>
  );
}

function TextSection({
  title,
  help,
  placeholder,
  value,
  onChange,
  rows = 3,
  mono,
  defaultOpen,
}: {
  title: string;
  help: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  mono?: boolean;
  defaultOpen?: boolean;
}): JSX.Element {
  return (
    <SectionShell title={title} filled={value.trim().length > 0} defaultOpen={defaultOpen}>
      <p className="text-xs text-muted-foreground">{help}</p>
      <Textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={mono ? 'font-mono text-xs' : 'text-sm'}
      />
    </SectionShell>
  );
}

function ListSection({
  title,
  help,
  placeholder,
  addLabel,
  items,
  onChange,
  defaultOpen,
}: {
  title: string;
  help: string;
  placeholder: string;
  addLabel: string;
  items: string[];
  onChange: (v: string[]) => void;
  defaultOpen?: boolean;
}): JSX.Element {
  const rows = items.length > 0 ? items : [''];
  return (
    <SectionShell
      title={title}
      filled={items.some((i) => i.trim().length > 0)}
      defaultOpen={defaultOpen}
    >
      <p className="text-xs text-muted-foreground">{help}</p>
      <ol className="space-y-2">
        {rows.map((item, idx) => (
          <li key={idx} className="flex items-center gap-2">
            <span className="w-5 text-right text-xs text-muted-foreground">{idx + 1}.</span>
            <Input
              value={item}
              onChange={(e) => onChange(rows.map((r, i) => (i === idx ? e.target.value : r)))}
              placeholder={placeholder}
              className="text-sm"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => onChange(rows.filter((_, i) => i !== idx))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ol>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="gap-1.5"
        onClick={() => onChange([...rows, ''])}
      >
        <Plus className="h-3.5 w-3.5" />
        {addLabel}
      </Button>
    </SectionShell>
  );
}

function ExamplesSection({
  examples,
  onChange,
}: {
  examples: BotPromptExample[];
  onChange: (v: BotPromptExample[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const update = (idx: number, patch: Partial<BotPromptExample>): void =>
    onChange(examples.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  return (
    <SectionShell
      title={t('organization.bots.dialog.prompt.examples.title')}
      filled={examples.some((e) => e.question.trim() && e.answer.trim())}
    >
      <p className="text-xs text-muted-foreground">
        {t('organization.bots.dialog.prompt.examples.help')}
      </p>
      {examples.map((ex, idx) => (
        <div key={idx} className="space-y-2 rounded-md border bg-muted/30 p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">
              {t('organization.bots.dialog.prompt.examples.item', { n: idx + 1 })}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => onChange(examples.filter((_, i) => i !== idx))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input
            value={ex.question}
            onChange={(e) => update(idx, { question: e.target.value })}
            placeholder={t('organization.bots.dialog.prompt.examples.questionPlaceholder')}
            className="text-sm"
          />
          <Textarea
            rows={3}
            value={ex.answer}
            onChange={(e) => update(idx, { answer: e.target.value })}
            placeholder={t('organization.bots.dialog.prompt.examples.answerPlaceholder')}
            className="text-sm"
          />
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="gap-1.5"
        disabled={examples.length >= 10}
        onClick={() => onChange([...examples, { question: '', answer: '' }])}
      >
        <Plus className="h-3.5 w-3.5" />
        {t('organization.bots.dialog.prompt.examples.add')}
      </Button>
    </SectionShell>
  );
}

function PromptPreviewPanel({ value }: { value: BotPromptSections }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const normalized = React.useMemo(() => normalizePromptSections(value), [value]);
  const [debounced, setDebounced] = React.useState(normalized);

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(normalized), 400);
    return () => window.clearTimeout(id);
  }, [normalized]);

  const isEmpty = Object.keys(debounced).length === 0;
  const preview = useQuery({
    queryKey: ['bot-prompt-preview', JSON.stringify(debounced)],
    queryFn: () => botsApi.previewPrompt(debounced),
    enabled: open && !isEmpty,
    staleTime: 60_000,
  });

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        {open
          ? t('organization.bots.dialog.prompt.preview.hide')
          : t('organization.bots.dialog.prompt.preview.show')}
      </button>
      {open ? (
        <div className="mt-2 space-y-2 rounded-md border bg-muted/40 p-3">
          {isEmpty ? (
            <p className="text-xs text-muted-foreground">
              {t('organization.bots.dialog.prompt.preview.empty')}
            </p>
          ) : null}
          {!isEmpty && preview.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('common.loading')}
            </div>
          ) : null}
          {!isEmpty && preview.error ? (
            <p className="text-xs text-destructive">{unwrapError(preview.error)}</p>
          ) : null}
          {!isEmpty && preview.data ? (
            <>
              <p
                className={
                  preview.data.length > preview.data.max
                    ? 'text-xs font-medium text-destructive'
                    : 'text-xs text-muted-foreground'
                }
              >
                {t('organization.bots.dialog.prompt.preview.count', {
                  length: preview.data.length,
                  max: preview.data.max,
                })}
              </p>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-background p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
                {preview.data.prompt}
              </pre>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
